import React from "react";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, SkipForward } from "lucide-react";

/**
 * Bottom action bar: Previous · step extras · Validate/Next (or Acknowledge / Ignore).
 * After a step passes validation, Validate renames to Next and advances the wizard.
 */
export default function PayrollStepActions({
  step,
  stepBusy,
  employeeCount,
  onRunCheck,
  onIgnore,
  onAcknowledge,
  onPrevious,
  onNext,
  canGoPrevious = false,
  canGoNext = false,
  nextBlocked = false,
  nextBlockedTitle = "Complete this step before continuing",
  showValidate = true,
  extraActions = null,
  darkMode = false,
  disabled = false,
  className = "",
}) {
  if (!step) return null;

  const ignored = step.resultJson?.meta?.ignoredForPayroll;
  const canIgnore =
    showValidate &&
    !ignored &&
    step.key !== "preview_validate" &&
    ["failed", "acknowledged"].includes(step.status);

  const canAcknowledge =
    showValidate &&
    step.status === "acknowledged" &&
    !ignored &&
    step.key === "attendance_validation";

  const locked = disabled || stepBusy;
  const validated = ["passed", "acknowledged"].includes(step.status);
  // When step is view-locked (processed/finalised), still allow Next/Previous navigation.
  const showNextButton = canGoNext && (!showValidate || validated || disabled);
  const nextDisabled = stepBusy || nextBlocked;

  return (
    <div
      className={`flex flex-wrap items-center justify-end gap-2 border-t pt-3 ${
        darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
      } ${className}`}
    >
      {canGoPrevious && (
        <button
          type="button"
          disabled={stepBusy}
          onClick={onPrevious}
          className={`mr-auto inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
            darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
          }`}
        >
          <ChevronLeft size={14} />
          Previous
        </button>
      )}
      {ignored && (
        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-sky-800">
          Ignored for payroll
        </span>
      )}
      {disabled && (
        <span className="rounded-full bg-[hsl(var(--ds-muted))] px-2.5 py-1 text-[10px] font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
          View only
        </span>
      )}
      {canAcknowledge && (
        <button
          type="button"
          disabled={locked}
          onClick={onAcknowledge}
          className={`rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
            darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          Acknowledge
        </button>
      )}
      {canIgnore && (
        <button
          type="button"
          disabled={locked}
          onClick={onIgnore}
          className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
            darkMode
              ? "border-amber-700/50 bg-amber-950/30 text-amber-200"
              : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
          title="Accept this issue and proceed with payroll without fixing now"
        >
          <SkipForward size={14} />
          Ignore &amp; proceed
        </button>
      )}
      {extraActions}
      {showValidate && !validated && !disabled && (
        <button
          type="button"
          disabled={locked || !employeeCount}
          onClick={onRunCheck}
          className="inline-flex items-center gap-1 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          title="Validate this step"
        >
          {stepBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Validate
        </button>
      )}
      {showNextButton && (
        <button
          type="button"
          disabled={nextDisabled}
          onClick={onNext}
          className="inline-flex items-center gap-1 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          title={nextBlocked ? nextBlockedTitle : "Go to next step"}
        >
          Next
          <ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}
